import React, { useEffect, useMemo, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import Colors from "../styles/Colors.json";
import "./SyncStatusPage.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import {
    ClientSyncRun,
    SyncDetailsJson,
    SyncRunStatus,
    ServiceOrderSyncResult,
    SERVICE_ORDER_SYNC_TYPE,
} from "../interfaces/SyncStatus";
import { getSyncStatus, getSyncStatusErrorMessage } from "../api/syncStatus";
import SyncStatusManager, { summarizeServiceOrderSync } from "../managers/SyncStatusManager";
import config from "../configLoader";
import {
    RefreshCw,
    ChevronRight,
    X,
    AlertTriangle,
    CheckCircle,
    Activity,
    Layers,
    Calendar,
    User,
    Clock,
    Play,
    Flag,
    Zap,
    FileWarning,
    FileCheck,
    Wrench,
    Info,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type CellHealth = "good" | "issue" | "cancelled" | "run" | "none";
type WindowDays = 7 | 14 | 30;

interface DayColumn {
    key: string;    // "2026-07-03"
    date: Date;
    dow: string;    // "Fri"
    label: string;  // "Jul 3"
}

interface ModalState {
    isOpen: boolean;
    dayDow: string;
    dayLabel: string;
    syncType: string;
    runs: ClientSyncRun[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_PRIORITY: Record<SyncRunStatus, number> = {
    FAILED: 4,
    CANCELLED: 3,
    RUNNING: 2,
    COMPLETED: 1,
};

const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function humanize(t: string): string {
    return t
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function getLocalDayKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/**
 * Relative-time helper.
 * No interpolation support in the app's translator, so we compose:
 *   e.g.  `5${tr('syncStatusMinutesAgoSuffix')}` → "5m ago"
 * This is the "compose" pattern documented for this page.
 */
function relTime(iso: string | null, tr: (k: string) => string): string {
    if (!iso) return "";
    const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diffSec < 45)    return tr("syncStatusJustNow");
    if (diffSec < 90)    return `1${tr("syncStatusMinutesAgoSuffix")}`;
    if (diffSec < 3600)  return `${Math.round(diffSec / 60)}${tr("syncStatusMinutesAgoSuffix")}`;
    if (diffSec < 5400)  return `1${tr("syncStatusHoursAgoSuffix")}`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}${tr("syncStatusHoursAgoSuffix")}`;
    const days = Math.round(diffSec / 86400);
    if (days === 1) return tr("syncStatusYesterday");
    if (days < 7)   return `${days}${tr("syncStatusDaysAgoSuffix")}`;
    return `${Math.floor(days / 7)}${tr("syncStatusWeeksAgoSuffix")}`;
}

function fmtDuration(s: number | null): string {
    if (s == null) return "—";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (m < 60) return r ? `${m}m ${r}s` : `${m}m`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm ? `${h}h ${rm}m` : `${h}h`;
}

function fmtLocalTime(utc: string): string {
    const d = new Date(utc);
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ap = h < 12 ? "AM" : "PM";
    h = h % 12 || 12;
    return `${h}:${min} ${ap}`;
}

function fmtLocalDateTime(utc: string): string {
    const d = new Date(utc);
    return `${MON_SHORT[d.getMonth()]} ${d.getDate()}, ${fmtLocalTime(utc)}`;
}

function getCellHealth(runs: ClientSyncRun[]): CellHealth {
    if (!runs.length) return "none";
    let worst: SyncRunStatus = runs[0].status;
    for (const r of runs) {
        if (STATUS_PRIORITY[r.status] > STATUS_PRIORITY[worst]) worst = r.status;
    }
    switch (worst) {
        case "FAILED":    return "issue";
        case "CANCELLED": return "cancelled";
        case "RUNNING":   return "run";
        default:          return "good";
    }
}

function isFlatObject(v: unknown): boolean {
    if (v == null || typeof v !== "object" || Array.isArray(v)) return false;
    return Object.values(v as Record<string, unknown>).every(
        (x) => x === null || typeof x !== "object"
    );
}

function escHtml(s: string): string {
    return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] ?? c));
}

function highlightJson(json: unknown): string {
    const text = JSON.stringify(json, null, 2);
    const e = escHtml(text);
    return e
        .replace(/"([^"]*)"(\s*:)/g,  '<span class="sy-jk">"$1"</span>$2')
        .replace(/:\s"([^"]*)"/g,      ': <span class="sy-js">"$1"</span>')
        .replace(/:\s(-?\d+\.?\d*)/g,  ': <span class="sy-jn">$1</span>')
        .replace(/:\s(true|false|null)/g, ': <span class="sy-jb">$1</span>');
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SkeletonMatrix: React.FC = () => (
    <div className="sy-matrix-card">
        <div className="sy-mx-wrap">
            <table className="sy-mx">
                <thead>
                    <tr>
                        <th className="sy-mx-corner">
                            <span className="sy-co-t" style={{ display: "block", height: 13 }} />
                        </th>
                        {Array.from({ length: 7 }).map((_, i) => (
                            <th key={i}>
                                <div className="sy-day-h">
                                    <span className="sy-sk-line sy-sk-sm" />
                                    <span className="sy-sk-line sy-sk-xs" style={{ marginTop: 3 }} />
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 4 }).map((_, r) => (
                        <tr key={r}>
                            <th className="sy-row-lab">
                                <span className="sy-sk-line sy-sk-lg" style={{ display: "block" }} />
                                <span className="sy-sk-line sy-sk-sm" style={{ display: "block", marginTop: 6 }} />
                            </th>
                            {Array.from({ length: 7 }).map((_, c) => (
                                <td key={c}><span className="sy-sk-cell" /></td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

interface RunCardProps {
    run: ClientSyncRun;
    tr: (k: string) => string;
}

function renderJsonSection(
    details: SyncDetailsJson,
    kind: "ok" | "err",
    tr: (k: string) => string
): JSX.Element | null {
    if (details === null) return null;

    const label = kind === "err" ? tr("syncStatusErrorDetails") : tr("syncStatusSuccessDetails");
    const headClass = `sy-det-head ${kind === "err" ? "sy-dh-err" : "sy-dh-ok"}`;

    let body: JSX.Element;
    if (typeof details === "string") {
        body = (
            <div className="sy-code-block">
                <pre dangerouslySetInnerHTML={{ __html: escHtml(details) }} />
            </div>
        );
    } else if (isFlatObject(details)) {
        body = (
            <div className="sy-kv-grid">
                {Object.entries(details as Record<string, unknown>).map(([k, v]) => {
                    const isNum  = typeof v === "number";
                    const isBool = typeof v === "boolean";
                    const vClass = isNum
                        ? "sy-num"
                        : isBool
                        ? `sy-bool${v ? "" : " sy-bool-f"}`
                        : "";
                    const display =
                        v === null  ? "—"
                        : isNum    ? (v as number).toLocaleString()
                        : isBool   ? String(v)
                        : String(v);
                    return (
                        <div key={k} className="sy-kv">
                            <span className="sy-kv-k">{k}</span>
                            <span className={`sy-kv-v ${vClass}`}>{display}</span>
                        </div>
                    );
                })}
            </div>
        );
    } else {
        body = (
            <div className="sy-code-block">
                <pre dangerouslySetInnerHTML={{ __html: highlightJson(details) }} />
            </div>
        );
    }

    return (
        <div className="sy-det-section">
            <div className={headClass}>
                {kind === "err" ? <FileWarning size={13} /> : <FileCheck size={13} />}
                {label}
            </div>
            {body}
        </div>
    );
}

const RunCard: React.FC<RunCardProps> = ({ run, tr }) => {
    const isRunning    = run.status === "RUNNING";
    const isScheduler  = !run.triggeredBy.includes("@");
    const pillClass    = `sy-run-pill sy-pill-${run.status.toLowerCase()}`;

    return (
        <div className="sy-run-card">
            <div className="sy-rc-top">
                <span className={pillClass}>
                    <span className="sy-pill-dot" />
                    {run.status}
                </span>
                <span className="sy-rc-run">#{run.clientSyncRunID}</span>
                <span className="sy-rc-trig">
                    {isScheduler ? <Clock size={13} /> : <User size={13} />}
                    {isScheduler ? (
                        <span>{humanize(run.triggeredBy)}</span>
                    ) : (
                        <span className="sy-tg-val">{run.triggeredBy}</span>
                    )}
                </span>
            </div>
            <div className="sy-rc-body">
                <div className="sy-meta-grid">
                    <div className="sy-mg-cell">
                        <div className="sy-mg-lab"><Play size={12} /> {tr("syncStatusStarted")}</div>
                        <div className="sy-mg-val">
                            {fmtLocalDateTime(run.startedAtUtc)}
                            <span className="sy-rel">{relTime(run.startedAtUtc, tr)}</span>
                        </div>
                    </div>
                    <div className="sy-mg-cell">
                        <div className="sy-mg-lab"><Flag size={12} /> {tr("syncStatusCompleted")}</div>
                        <div className="sy-mg-val">
                            {run.completedAtUtc ? (
                                <>
                                    {fmtLocalDateTime(run.completedAtUtc)}
                                    <span className="sy-rel">{relTime(run.completedAtUtc, tr)}</span>
                                </>
                            ) : (
                                <span className="sy-dash">—</span>
                            )}
                        </div>
                    </div>
                    <div className="sy-mg-cell">
                        <div className="sy-mg-lab"><Clock size={12} /> {tr("syncStatusDuration")}</div>
                        <div className="sy-mg-val">
                            {run.durationSeconds != null
                                ? fmtDuration(run.durationSeconds)
                                : <span className="sy-dash">—</span>}
                        </div>
                    </div>
                    <div className="sy-mg-cell">
                        <div className="sy-mg-lab"><Zap size={12} /> {tr("syncStatusTriggeredBy")}</div>
                        <div className="sy-mg-val sy-mg-trig">
                            {isScheduler ? humanize(run.triggeredBy) : run.triggeredBy}
                        </div>
                    </div>
                </div>

                {isRunning && (
                    <div className="sy-running-note">
                        <span className="sy-spin-sm" />
                        {tr("syncStatusStillRunning")}
                    </div>
                )}

                {run.errorMessage && (
                    <div className="sy-run-err">
                        <AlertTriangle size={17} />
                        <div>
                            <b>{tr("syncStatusErrorMessage")}</b>
                            {run.errorMessage}
                        </div>
                    </div>
                )}

                {renderJsonSection(run.errorDetailsJson, "err", tr)}
                {renderJsonSection(run.successDetailsJson, "ok", tr)}
            </div>
        </div>
    );
};

// ─── Main page component ──────────────────────────────────────────────────────

const SyncStatusPage: React.FC = () => {
    // IMPORTANT: do NOT use setLoading from SiteContext on this page.
    // Using it causes an infinite-loop hazard when put in effect dep arrays.
    // createPopup is used ONLY in the access-gate effect below.
    const { createPopup, userRole, setPage, translator } = useSiteContext();
    const tr = (k: string): string => translator.translate(k) as string;

    // ── Local state ──────────────────────────────────────────────────────────
    const [runs, setRuns]                   = useState<ClientSyncRun[]>([]);
    const [isLoading, setIsLoading]         = useState(true);   // true → show skeleton initially
    const [loadError, setLoadError]         = useState<string | null>(null);
    const [hasEverLoaded, setHasEverLoaded] = useState(false);
    const [days, setDays]                   = useState<WindowDays>(7);
    const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
    const [autoRefresh, setAutoRefresh]     = useState(false);
    const [toast, setToast]                 = useState<{ id: number; msg: string } | null>(null);
    const [modalState, setModalState]       = useState<ModalState>({
        isOpen: false, dayDow: "", dayLabel: "", syncType: "", runs: [],
    });
    // TAS-516 — the on-demand service-order sync. `soSyncing` covers only the request this
    // page fired; a run started elsewhere (the nightly timer, another admin) is caught by
    // `soRunInFlight` below, off the run list this page already polls.
    const [soSyncing, setSoSyncing]         = useState(false);
    const [soResult, setSoResult]           = useState<ServiceOrderSyncResult | null>(null);

    // ── Refs (avoid stale closures in intervals) ─────────────────────────────
    const isFetchingRef     = useRef(false);
    const hasEverLoadedRef  = useRef(false);
    const daysRef           = useRef<WindowDays>(7);
    const autoTimerRef      = useRef<ReturnType<typeof setInterval> | null>(null);
    const toastTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Keep refs in sync with state
    useEffect(() => { daysRef.current = days; }, [days]);
    useEffect(() => { hasEverLoadedRef.current = hasEverLoaded; }, [hasEverLoaded]);

    // ── Toast helper ─────────────────────────────────────────────────────────
    const showToast = (msg: string) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ id: Date.now(), msg });
        toastTimerRef.current = setTimeout(() => setToast(null), 4500);
    };

    // ── Data loader ──────────────────────────────────────────────────────────
    // loadDataRef keeps a stable reference to the latest loadData closure so the
    // auto-refresh interval always calls the up-to-date version without needing
    // to be in any effect dep array.
    const loadDataRef = useRef<(windowDays: number, isAuto?: boolean) => Promise<void>>();

    const loadData = async (windowDays: number, isAuto = false): Promise<void> => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        if (!isAuto) {
            setLoadError(null);
            setIsLoading(true);
        }

        try {
            const data = await getSyncStatus(windowDays);
            setRuns(data);
            setLastRefreshed(new Date());
            setLoadError(null);
            if (!hasEverLoadedRef.current) {
                setHasEverLoaded(true);
                hasEverLoadedRef.current = true;
            }
        } catch (err) {
            const msg = getSyncStatusErrorMessage(err);
            // Auto-refresh: if data already renders, show toast instead of error state.
            // Manual load / initial load: always use full error state.
            if (isAuto && hasEverLoadedRef.current) {
                showToast(msg);
            } else {
                setLoadError(msg);
            }
        } finally {
            isFetchingRef.current = false;
            if (!isAuto) setIsLoading(false);
        }
    };

    // Always keep loadDataRef pointing at the current function
    // (no-op re-assignment each render is intentional)
    loadDataRef.current = loadData;

    // ── Access gate ──────────────────────────────────────────────────────────
    // createPopup / setPage deliberately omitted from dep array — they are stable
    // context refs; including them causes the infinite-loop hazard documented in
    // TimeOffConfigurationPage.tsx.
    useEffect(() => {
        if (!userRole.admin || !config.showSyncStatusPage) {
            createPopup("Access Denied", "You do not have permission to access this page.");
            setPage(Page.Dashboard);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole.admin]);

    // ── Initial load ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userRole.admin || !config.showSyncStatusPage) return;
        loadData(days);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole.admin]);

    // ── Auto-refresh interval ─────────────────────────────────────────────────
    useEffect(() => {
        if (autoTimerRef.current) {
            clearInterval(autoTimerRef.current);
            autoTimerRef.current = null;
        }
        if (autoRefresh) {
            autoTimerRef.current = setInterval(() => {
                // Skip if tab is hidden or a fetch is already in progress
                if (document.visibilityState === "hidden") return;
                if (loadDataRef.current) loadDataRef.current(daysRef.current, true);
            }, 20000);
        }
        return () => {
            if (autoTimerRef.current) clearInterval(autoTimerRef.current);
        };
    }, [autoRefresh]);

    // ── Cleanup toast timer on unmount ────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // ── Esc to close modal ────────────────────────────────────────────────────
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") setModalState((prev) => ({ ...prev, isOpen: false }));
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, []);

    // ── Matrix computation ────────────────────────────────────────────────────
    const { dayColumns, syncTypes, runsByDayAndType } = useMemo(() => {
        const dayDateMap = new Map<string, Date>();
        const typeSet    = new Set<string>();

        runs.forEach((run) => {
            const d = new Date(run.startedAtUtc);
            const k = getLocalDayKey(d);
            if (!dayDateMap.has(k)) {
                dayDateMap.set(k, new Date(d.getFullYear(), d.getMonth(), d.getDate()));
            }
            typeSet.add(run.syncType);
        });

        const dayColumns: DayColumn[] = Array.from(dayDateMap.entries())
            .sort((a, b) => b[1].getTime() - a[1].getTime())
            .map(([key, date]) => ({
                key,
                date,
                dow:   DOW_SHORT[date.getDay()],
                label: `${MON_SHORT[date.getMonth()]} ${date.getDate()}`,
            }));

        const syncTypes = Array.from(typeSet).sort((a, b) => a.localeCompare(b));

        // runsByDayAndType: dayKey → syncType → runs[] (each cell sorted newest-first)
        const runsByDayAndType = new Map<string, Map<string, ClientSyncRun[]>>();
        runs.forEach((run) => {
            const dk = getLocalDayKey(new Date(run.startedAtUtc));
            if (!runsByDayAndType.has(dk)) runsByDayAndType.set(dk, new Map());
            const dayMap = runsByDayAndType.get(dk)!;
            if (!dayMap.has(run.syncType)) dayMap.set(run.syncType, []);
            dayMap.get(run.syncType)!.push(run);
        });
        runsByDayAndType.forEach((dayMap) => {
            dayMap.forEach((cellRuns) => {
                cellRuns.sort(
                    (a, b) => new Date(b.startedAtUtc).getTime() - new Date(a.startedAtUtc).getTime()
                );
            });
        });

        return { dayColumns, syncTypes, runsByDayAndType };
    }, [runs]);

    // ── Count strip stats ─────────────────────────────────────────────────────
    const countStats = useMemo(() => {
        const failCount   = runs.filter((r) => r.status === "FAILED").length;
        const failDays    = new Set(
            runs.filter((r) => r.status === "FAILED")
                .map((r) => getLocalDayKey(new Date(r.startedAtUtc)))
        ).size;
        const runningCount = runs.filter((r) => r.status === "RUNNING").length;
        return { failCount, failDays, runningCount };
    }, [runs]);

    // TAS-516 — a SERVICEORDERS run already in flight, whoever started it: this button,
    // the nightly timer, or another admin. The page already polls the run list, so this
    // needs no extra endpoint, and it keeps two dispatchers from stacking full re-syncs.
    // Case-insensitive because syncType is a free-form string column, not an enum.
    const soRunInFlight = useMemo(
        () => runs.some(
            (r) => r.status === "RUNNING"
                && r.syncType?.toUpperCase() === SERVICE_ORDER_SYNC_TYPE
        ),
        [runs]
    );
    const soBusy = soSyncing || soRunInFlight;

    // ── Today's key ───────────────────────────────────────────────────────────
    const todayKey = useMemo(() => getLocalDayKey(new Date()), []);

    // ── Row meta line ──────────────────────────────────────────────────────────
    const getRowMeta = (syncType: string): string => {
        const rowRuns: ClientSyncRun[] = [];
        runsByDayAndType.forEach((dayMap) => {
            const cellRuns = dayMap.get(syncType);
            if (cellRuns) rowRuns.push(...cellRuns);
        });
        const total      = rowRuns.length;
        const rowRunning = rowRuns.some((r) => r.status === "RUNNING");
        const rowFails   = rowRuns.filter((r) => r.status === "FAILED").length;
        const lastRun    = rowRuns.reduce<ClientSyncRun | null>((acc, r) => {
            if (!acc) return r;
            return new Date(r.startedAtUtc) > new Date(acc.startedAtUtc) ? r : acc;
        }, null);

        let meta = `${total} run${total === 1 ? "" : "s"}`;
        if (rowRunning)      meta += " · running now";
        else if (rowFails)   meta += ` · ${rowFails} failed`;
        else if (lastRun)    meta += ` · last ${relTime(lastRun.startedAtUtc, tr)}`;
        return meta;
    };

    // ── Actions ────────────────────────────────────────────────────────────────
    const handleWindowChange = (newDays: WindowDays) => {
        if (newDays === days) return;
        setDays(newDays);
        loadData(newDays);
    };

    const handleRefresh = () => {
        loadData(days);
    };

    const handleWiden = () => {
        handleWindowChange(30);
    };

    /**
     * TAS-516 — run the Vista SM work-order + scope sync now.
     *
     * Reloads the run list afterwards either way, so the SERVICEORDERS row on the matrix
     * below picks up the run this button just created (successful or failed) without the
     * user having to hit Refresh.
     */
    const handleServiceOrderSync = async () => {
        if (soSyncing) return;
        setSoSyncing(true);
        setSoResult(null);
        try {
            setSoResult(await SyncStatusManager.syncServiceOrders());
        } finally {
            setSoSyncing(false);
            await loadData(days);
        }
    };

    const openModal = (day: DayColumn, syncType: string) => {
        const cellRuns = runsByDayAndType.get(day.key)?.get(syncType) ?? [];
        setModalState({
            isOpen: true,
            dayDow: day.dow,
            dayLabel: day.label,
            syncType,
            runs: cellRuns,
        });
    };

    const closeModal = () => setModalState((prev) => ({ ...prev, isOpen: false }));

    // ── View-state booleans ───────────────────────────────────────────────────
    const showSkeleton  = isLoading && !hasEverLoaded;
    const showRefreshing = isLoading && hasEverLoaded;   // skeleton cells over existing matrix
    const showError     = !isLoading && loadError !== null;
    const showEmpty     = !isLoading && !loadError && hasEverLoaded && runs.length === 0;
    const showMatrix    = (hasEverLoaded && runs.length > 0) || showRefreshing;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <PageWrapper title={tr("syncStatus")} backgroundColor={Colors.White}>
            <div className="syncv2">
                <div className="sy-page">
                    {/* ── Breadcrumb ─────────────────────────────────────── */}
                    <div className="sy-crumbs">
                        <span>{tr("syncStatusPayrollSync")}</span>
                        <ChevronRight size={14} />
                        <span className="sy-here">{tr("syncStatus")}</span>
                    </div>

                    {/* ── Page header ────────────────────────────────────── */}
                    <div className="sy-app-head">
                        <div className="sy-logo">
                            <RefreshCw size={23} />
                        </div>
                        <div className="sy-head-text">
                            <h1 className="sy-app-title">{tr("syncStatus")}</h1>
                            <div className="sy-app-sub">{tr("syncStatusSubtitle")}</div>
                        </div>
                        <div className="sy-head-right">
                            {lastRefreshed && (
                                <div className="sy-refreshed">
                                    <span className="sy-rf-lab">{tr("syncStatusLastRefreshed")}</span>
                                    <span className="sy-rf-val">
                                        {fmtLocalTime(lastRefreshed.toISOString())}
                                        {" · "}
                                        {relTime(lastRefreshed.toISOString(), tr)}
                                    </span>
                                </div>
                            )}
                            <button
                                className="sy-btn"
                                onClick={handleRefresh}
                                disabled={isLoading}
                                type="button"
                            >
                                <RefreshCw size={15} className={isLoading ? "sy-spin" : ""} />
                                {tr("syncStatusRefresh")}
                            </button>
                        </div>
                    </div>

                    {/* ── Control bar ────────────────────────────────────── */}
                    <div className="sy-control-bar">
                        <div className="sy-field">
                            <div className="sy-flab">{tr("syncStatusTimeWindow")}</div>
                            <div className="sy-segmented">
                                {([7, 14, 30] as const).map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={days === n ? "on" : ""}
                                        onClick={() => handleWindowChange(n)}
                                        disabled={isLoading}
                                    >
                                        {tr(`syncStatusWindow${n}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="sy-autoref">
                            <label className="sy-switch">
                                <input
                                    type="checkbox"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                />
                                <span className="sy-track" />
                            </label>
                            <div className="sy-ar-txt">
                                <span className="sy-ar-t">{tr("syncStatusAutoRefresh")}</span>
                                <span className="sy-ar-s">{tr("syncStatusAutoRefreshHint")}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── TAS-516: Sync Service Orders (NPM) ─────────────────
                        The matrix below is where the SERVICEORDERS run this button
                        creates shows up, which is why the button lives here rather
                        than on a page that would have to link to this one. */}
                    {config.showServiceOrderSync && (
                        <div className="sy-so-panel">
                            <div className="sy-so-main">
                                <div className="sy-so-text">
                                    <div className="sy-so-title">
                                        <Wrench size={15} />
                                        Service Orders
                                    </div>
                                    <div className="sy-so-help">
                                        Vista's data feed can lag up to an hour after a Service Order
                                        is entered; if a number still isn't found, sync again later.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="sy-btn sy-btn-primary"
                                    onClick={handleServiceOrderSync}
                                    disabled={soBusy}
                                >
                                    <RefreshCw size={15} className={soBusy ? "sy-spin" : ""} />
                                    {soSyncing
                                        ? "Syncing…"
                                        : soRunInFlight
                                            ? "Sync already running"
                                            : "Sync Service Orders"}
                                </button>
                            </div>

                            {soResult && (
                                <div className={`sy-so-result${soResult.success ? "" : " err"}`}>
                                    <div className="sy-so-result-head">
                                        {soResult.success
                                            ? <CheckCircle size={14} />
                                            : <AlertTriangle size={14} />}
                                        <span>
                                            {soResult.message
                                                || soResult.errorMessage
                                                || (soResult.success ? "Service orders synced." : "Service order sync failed.")}
                                        </span>
                                        <button
                                            type="button"
                                            className="sy-so-dismiss"
                                            title="Dismiss"
                                            onClick={() => setSoResult(null)}
                                        >
                                            <X size={13} />
                                        </button>
                                    </div>

                                    {soResult.success && soResult.details && (
                                        <>
                                            <dl className="sy-so-counts">
                                                {summarizeServiceOrderSync(soResult.details).map((c) => (
                                                    <div key={c.label}>
                                                        <dt>{c.label}</dt>
                                                        <dd>{c.value.toLocaleString()}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                            {/* Vista SM companies 2 and 3 are synced together but
                                                reported apart, so a failure confined to one company
                                                is visible instead of averaged away by the totals. */}
                                            <div className="sy-so-cos">
                                                {([["Company 2", soResult.details.company2],
                                                   ["Company 3", soResult.details.company3]] as const)
                                                    .filter(([, d]) => d != null)
                                                    .map(([label, d]) => (
                                                        <span key={label}>
                                                            <b>{label}</b> {d.inserted} new · {d.updated} updated ·{" "}
                                                            {d.unchanged} unchanged · {d.scopesInserted + d.scopesUpdated} scopes
                                                            {d.errors?.length > 0 && ` · ${d.errors.length} error${d.errors.length === 1 ? "" : "s"}`}
                                                        </span>
                                                    ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Loading skeleton (initial load, no data yet) ─── */}
                    {showSkeleton && <SkeletonMatrix />}

                    {/* ── Load error ─────────────────────────────────────── */}
                    {showError && (
                        <div className="sy-load-error">
                            <div className="sy-le-ic">
                                <AlertTriangle size={23} />
                            </div>
                            <div className="sy-le-main">
                                <div className="sy-le-t">{tr("syncStatusCantLoad")}</div>
                                <div className="sy-le-msg">{loadError}</div>
                                <div className="sy-le-act">
                                    <button
                                        type="button"
                                        className="sy-btn sy-btn-primary"
                                        onClick={handleRefresh}
                                    >
                                        <RefreshCw size={15} />
                                        {tr("syncStatusRefresh")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Matrix, count strip, legend, empty state ────────── */}
                    {!showSkeleton && !showError && (
                        <>
                            {showMatrix && (
                                <>
                                    {/* Count strip */}
                                    <div className="sy-count-strip">
                                        <div
                                            className={`sy-count-cell sy-cc-fail${
                                                countStats.failCount === 0 ? " sy-zero" : ""
                                            }`}
                                        >
                                            <div className="sy-cc-lab">
                                                {countStats.failCount === 0 ? (
                                                    <CheckCircle size={13} />
                                                ) : (
                                                    <AlertTriangle size={13} />
                                                )}
                                                {tr("syncStatusFailuresInWindow")}
                                            </div>
                                            <div className="sy-cc-val">{countStats.failCount}</div>
                                            <div className="sy-cc-sub">
                                                {countStats.failCount === 0
                                                    ? tr("syncStatusAllClear")
                                                    : `${tr("syncStatusAcrossPrefix")}${countStats.failDays} ${tr(
                                                          countStats.failDays === 1 ? "syncStatusDaySingular" : "syncStatusDayPlural"
                                                      )}`}
                                            </div>
                                        </div>
                                        <div
                                            className={`sy-count-cell sy-cc-run${
                                                countStats.runningCount === 0 ? " sy-zero" : ""
                                            }`}
                                        >
                                            <div className="sy-cc-lab">
                                                <Activity size={13} />
                                                {tr("syncStatusRunningNow")}
                                            </div>
                                            <div className="sy-cc-val">{countStats.runningCount}</div>
                                            <div className="sy-cc-sub">
                                                {countStats.runningCount === 0
                                                    ? tr("syncStatusNothingInProgress")
                                                    : tr("syncStatusInProgressNow")}
                                            </div>
                                        </div>
                                        <div className="sy-count-cell sy-cc-types">
                                            <div className="sy-cc-lab">
                                                <Layers size={13} />
                                                {tr("syncStatusSyncTypes")}
                                            </div>
                                            <div className="sy-cc-val">{syncTypes.length}</div>
                                            <div className="sy-cc-sub">
                                                {tr("syncStatusOverPrefix")}
                                                {dayColumns.length}{" "}
                                                {tr(dayColumns.length === 1 ? "syncStatusDaySingular" : "syncStatusDayPlural")}
                                            </div>
                                        </div>
                                        <div className="sy-count-cell sy-cc-runs">
                                            <div className="sy-cc-lab">
                                                <RefreshCw size={13} />
                                                {tr("syncStatusRunsInWindow")}
                                            </div>
                                            <div className="sy-cc-val">{runs.length}</div>
                                            <div className="sy-cc-sub">
                                                {tr("syncStatusLastPrefix")}
                                                {days} {tr("syncStatusDayPlural")}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Legend */}
                                    <div className="sy-legend">
                                        <span className="sy-lg-lab">{tr("syncStatusLegend")}</span>
                                        <span className="sy-lg-item sy-lg-good">
                                            <span className="sy-dot" />
                                            {tr("syncStatusHealthy")}
                                        </span>
                                        <span className="sy-lg-item sy-lg-issue">
                                            <span className="sy-dot" />
                                            {tr("syncStatusIssue")}
                                        </span>
                                        <span className="sy-lg-item sy-lg-run">
                                            <span className="sy-dot" />
                                            {tr("syncStatusRunning")}
                                        </span>
                                        <span className="sy-lg-item sy-lg-nodata">
                                            <span className="sy-dot" />
                                            {tr("syncStatusNoData")}
                                        </span>
                                        <span className="sy-lg-note">
                                            <Info size={13} />
                                            {tr("syncStatusWorstStatusNote")}
                                        </span>
                                    </div>

                                    {/* Matrix card */}
                                    <div className="sy-matrix-card">
                                        <div className="sy-mx-wrap">
                                            <table className="sy-mx">
                                                <thead>
                                                    <tr>
                                                        <th className="sy-mx-corner">
                                                            <span className="sy-co-t">
                                                                {tr("syncStatusSyncProcess")}
                                                            </span>
                                                        </th>
                                                        {dayColumns.map((day) => {
                                                            const isToday = day.key === todayKey;
                                                            return (
                                                                <th
                                                                    key={day.key}
                                                                    className={isToday ? "sy-today" : ""}
                                                                >
                                                                    <div className="sy-day-h">
                                                                        <span className="sy-dh-dow">
                                                                            {day.dow}
                                                                        </span>
                                                                        <span className="sy-dh-date">
                                                                            {day.label}
                                                                        </span>
                                                                        {isToday && (
                                                                            <span className="sy-today-badge">
                                                                                <span className="sy-dot" />
                                                                                {tr("syncStatusToday")}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </th>
                                                            );
                                                        })}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {syncTypes.map((syncType) => (
                                                        <tr key={syncType}>
                                                            <th className="sy-row-lab">
                                                                <div className="sy-rl-name">
                                                                    {humanize(syncType)}
                                                                </div>
                                                                <span className="sy-rl-type">
                                                                    {syncType}
                                                                </span>
                                                                <div className="sy-rl-meta">
                                                                    {getRowMeta(syncType)}
                                                                </div>
                                                            </th>
                                                            {dayColumns.map((day) => {
                                                                const isToday = day.key === todayKey;
                                                                const cellRuns =
                                                                    runsByDayAndType
                                                                        .get(day.key)
                                                                        ?.get(syncType) ?? [];
                                                                const health = getCellHealth(cellRuns);

                                                                return (
                                                                    <td
                                                                        key={day.key}
                                                                        className={
                                                                            isToday ? "sy-today" : ""
                                                                        }
                                                                    >
                                                                        {showRefreshing ? (
                                                                            <span className="sy-sk-cell" />
                                                                        ) : cellRuns.length === 0 ? (
                                                                            <span className="sy-cell sy-cell-empty">
                                                                                <span className="sy-c-dash">
                                                                                    –
                                                                                </span>
                                                                            </span>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className={`sy-cell sy-cell-${health}`}
                                                                                onClick={() =>
                                                                                    openModal(day, syncType)
                                                                                }
                                                                                title={`${humanize(
                                                                                    syncType
                                                                                )} · ${day.dow}, ${
                                                                                    day.label
                                                                                } — ${cellRuns.length} run${
                                                                                    cellRuns.length === 1
                                                                                        ? ""
                                                                                        : "s"
                                                                                }`}
                                                                            >
                                                                                <span className="sy-c-dot" />
                                                                                <span className="sy-c-runs">
                                                                                    {cellRuns.length} run
                                                                                    {cellRuns.length === 1
                                                                                        ? ""
                                                                                        : "s"}
                                                                                </span>
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Empty state */}
                            {showEmpty && (
                                <div className="sy-empty-state">
                                    <div className="sy-es-ic">
                                        <RefreshCw size={26} />
                                    </div>
                                    <div className="sy-es-t">{tr("syncStatusNoRunsTitle")}</div>
                                    <p>{tr("syncStatusEmpty")}</p>
                                    <button
                                        type="button"
                                        className="sy-btn"
                                        onClick={handleWiden}
                                    >
                                        <Calendar size={15} />
                                        {tr("syncStatusWidenWindow")}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Run details modal ─────────────────────────────────── */}
                {modalState.isOpen && (
                    <div
                        className="sy-modal-bg"
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) closeModal();
                        }}
                    >
                        <div className="sy-modal">
                            <div className="sy-modal-head">
                                <div className="sy-modal-titles">
                                    <h3 className="sy-modal-title">
                                        {humanize(modalState.syncType)}
                                    </h3>
                                    <div className="sy-mh-cellinfo">
                                        <span className="sy-mc-type">{modalState.syncType}</span>
                                        <span className="sy-mc-day">
                                            {modalState.dayDow}, {modalState.dayLabel}
                                            {" · "}
                                            {modalState.runs.length} run
                                            {modalState.runs.length === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="sy-modal-close"
                                    onClick={closeModal}
                                    aria-label="Close"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="sy-modal-body">
                                {modalState.runs.map((run) => (
                                    <RunCard key={run.clientSyncRunID} run={run} tr={tr} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Error toast (auto-refresh failures) ──────────────── */}
                {toast && (
                    <div className="sy-toast sy-toast-err" key={toast.id}>
                        <AlertTriangle size={15} />
                        <span>{toast.msg}</span>
                        <button type="button" onClick={() => setToast(null)}>
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

export default SyncStatusPage;
